import { useState, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MapPin, Search, ChevronDown, Check } from 'lucide-react';
import { API } from '../config/api';

export default function NeighborhoodSelect({ value, onChange, required = false, error }) {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const res = await fetch(`${API}/neighborhoods`);
        if (res.ok) {
          const data = await res.json();
          setNeighborhoods(data.neighborhoods || []);
        }
      } catch (err) {
        console.error('Failed to fetch neighborhoods:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNeighborhoods();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return neighborhoods;
    const q = search.toLowerCase();
    return neighborhoods.filter(n =>
      n.name.toLowerCase().includes(q) ||
      n.city.toLowerCase().includes(q) ||
      n.state.toLowerCase().includes(q)
    );
  }, [neighborhoods, search]);

  const grouped = useMemo(() => {
    const groups = {};
    const stateLabels = { DC: 'Washington DC', MD: 'Maryland', VA: 'Virginia', DMV: 'Other' };
    const order = ['DC', 'MD', 'VA', 'DMV'];
    for (const n of filtered) {
      const s = n.state;
      if (!groups[s]) groups[s] = { label: stateLabels[s] || s, items: [] };
      groups[s].items.push(n);
    }
    return order.filter(s => groups[s]).map(s => groups[s]);
  }, [filtered]);

  const selected = neighborhoods.find(n => n.neighborhood_id === value);

  const handleSelect = (hood) => {
    onChange(hood.neighborhood_id, hood);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="space-y-1.5 relative" data-testid="neighborhood-select">
      <Label className="flex items-center gap-1">
        <MapPin className="w-3.5 h-3.5 text-orange-400" />
        Neighborhood {required && <span className="text-red-400">*</span>}
      </Label>

      {/* Selected display / trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left transition-colors
          ${error ? 'border-2 border-red-500 bg-red-500/5' : 'border border-border bg-muted/30 hover:bg-muted/50'}
          ${isOpen ? 'ring-2 ring-orange-500/50' : ''}`}
        data-testid="neighborhood-trigger"
      >
        {selected ? (
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span className="font-medium">{selected.name}</span>
            <Badge className="text-[10px] bg-muted/50 text-muted-foreground">{selected.state}</Badge>
          </span>
        ) : (
          <span className="text-muted-foreground">Select your neighborhood...</span>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {error && <p className="text-xs text-red-400">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 rounded-lg border border-border bg-card shadow-xl max-h-[300px] overflow-hidden"
          data-testid="neighborhood-dropdown">
          {/* Search input */}
          <div className="p-2 border-b border-border sticky top-0 bg-card z-10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search neighborhoods..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-8 text-sm bg-muted/30 border-border"
                autoFocus
                data-testid="neighborhood-search"
              />
            </div>
          </div>

          {/* Results */}
          <div className="overflow-y-auto max-h-[240px]">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading neighborhoods...</div>
            ) : grouped.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">No neighborhoods found</div>
            ) : (
              grouped.map(group => (
                <div key={group.label}>
                  <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/20 sticky top-0">
                    {group.label}
                  </div>
                  {group.items.map(hood => (
                    <button
                      key={hood.neighborhood_id}
                      type="button"
                      onClick={() => handleSelect(hood)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/30 transition-colors text-left
                        ${value === hood.neighborhood_id ? 'bg-orange-500/10 text-orange-400' : ''}`}
                      data-testid={`hood-option-${hood.slug}`}
                    >
                      <span>{hood.name}</span>
                      {value === hood.neighborhood_id && <Check className="w-4 h-4 text-orange-400" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Click-outside handler */}
      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => { setIsOpen(false); setSearch(''); }} />
      )}
    </div>
  );
}
